import { type FragmentType, graphql, useFragment } from "@repo/graphql-types";
import { Bell } from "@repo/icons";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { VariablesOf } from "@graphql-typed-document-node/core";
import { ListRoot } from "@/components/list";
import { FollowUpAppointmentReminderListItem } from "@/components/follow-up-appointment-reminder-list-item";
import {
  EmptyStateRoot,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
  EmptyStateButton,
} from "@/components/empty-state";
import {
  PageTitleDescription,
  PageTitleMainContent,
  PageTitleRoot,
} from "@/components/page-title/page-title-root";
import { SearchInput } from "@/components/search-input";

const FollowUpAppointmentReminderListQuery = graphql(/* GraphQL */ `
  fragment FollowUpAppointmentReminderListQuery on query_root {
    lembretes(
      where: {
        codTipoLembrete: { _eq: $codTipoLembreteRetorno }
        ativo: { _eq: true }
        dataEnviado: { _is_null: true }
        dataParaEnvio: { _gte: $hoje }
      }
      order_by: { dataParaEnvio: asc }
    ) {
      codLembrete
      paciente {
        nome
      }
      usuarioCompromisso {
        nome
      }
      ...FollowUpAppointmentReminderListItemQuery
    }
  }
`);

interface FollowUpAppointmentReminderListProps {
  data: FragmentType<typeof FollowUpAppointmentReminderListQuery>;
}

export const FollowUpAppointmentReminderList = ({
  data,
}: FollowUpAppointmentReminderListProps): JSX.Element => {
  const queryData = useFragment(FollowUpAppointmentReminderListQuery, data);

  const [items, setItems] = useState<
    VariablesOf<typeof FollowUpAppointmentReminderListQuery>[] | null
  >(null);

  const filteredReminders = useMemo(() => {
    if (!items) return queryData.lembretes;

    return queryData.lembretes.filter((lembrete) => items.includes(lembrete));
  }, [queryData.lembretes, items]);

  return (
    <>
      {queryData.lembretes.length ? (
        <>
          <PageTitleRoot>
            <PageTitleMainContent>
              Confira e configure o envio automático de lembretes
            </PageTitleMainContent>
            <PageTitleDescription>
              Seu paciente será notificado no momento ideal para marcar uma consulta.
            </PageTitleDescription>
          </PageTitleRoot>
          <div className="mb-4">
            <SearchInput
              items={queryData.lembretes}
              onSearch={(results) =>
                setItems(results.length === queryData.lembretes.length ? null : results)
              }
              searchKeys={["paciente.nome", "usuarioCompromisso.nome"]}
              placeholder="Buscar por nome"
            />
          </div>
          <ListRoot className="pb-16">
            {filteredReminders.map((lembrete) => (
              <FollowUpAppointmentReminderListItem
                key={lembrete.codLembrete}
                data={lembrete}
              />
            ))}
          </ListRoot>

          <div className="fixed w-full bottom-0 left-0 px-4 py-6 ">
            <Link
              to="/settings/assist/follow-up-appointment-reminder/patients/"
              className="w-full"
            >
              <EmptyStateButton fullWidth>Criar lembrete</EmptyStateButton>
            </Link>
          </div>
        </>
      ) : (
        <EmptyStateRoot>
          <EmptyStateIcon icon={Bell} fill="primary" />
          <EmptyStateTitle>Você ainda não tem lembretes programados</EmptyStateTitle>
          <EmptyStateDescription>
            Crie um novo lembrete para os pacientes clicando no botão abaixo.
          </EmptyStateDescription>

          <Link
            to="/settings/assist/follow-up-appointment-reminder/patients/"
            className="w-full"
          >
            <EmptyStateButton fullWidth>Criar lembrete</EmptyStateButton>
          </Link>
        </EmptyStateRoot>
      )}
    </>
  );
};
